import React, { useState } from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { Save } from '@mui/icons-material';
import { colors } from '../../themes/typography';
import { experimentColors } from '../../themes/experimentColors';
import ComboDropdown from './ComboDropdown';
import ProductSelectionInline from './ProductSelectionInline';
import { alternativeOptions, useReplacementAlternatives } from './ReplacementAlternatives';

/**
 * Replacement section component for reemplazo_producto experiments
 * Shows product + candidate selection without discount functionality
 */
const ReplacementSection = ({
  item,
  productKey,
  isEditable,
  selectedCombo,
  experiment,
  updatedStates,
  onComboSelect,
  onSaveChanges,
  onSelectNewProduct,
  pendingSaves,
  productData,
  t,
  productName, // Product name from adapter
  clientId,
  // Product selection props
  showProductSelection,
  onCustomProductSelect,
  onCancelProductSelection
}) => {

  // Get candidate name for use in both display and save button
  const candidates = productData?.[productKey]?.candidates ||
    (experiment?.candidateNames || []).map(name => ({ name }));

  const candidateName = selectedCombo?.name ||
    (candidates.length > 0 ? candidates[0].name : null);

  const isLaneExpansion = experiment?.experimentType === 'ampliar_carril';
  const laneCount = experiment?.slots?.length || 0;

  // Reemplazo: alternativas por margen / por volumen y cuadrante de la máquina, pedidas la primera
  // vez que se abre el desplegable (no una petición por tarjeta del kanban al montar). Sin comboPrices
  // el guardado (useDiscountManagement) no puede resolver la elección, igual que "Select new product".
  const isReplacement = experiment?.experimentType === 'reemplazo_producto' && experiment?.comboPrices?.length > 0;
  const [alternativesOpened, setAlternativesOpened] = useState(false);
  const replacementAlternatives = useReplacementAlternatives(
    clientId || experiment?.clientId,
    experiment?.id,
    isReplacement && alternativesOpened
  );

  return (
    <Box sx={{ 
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '8px',
      p: '6px',
      mb: '6px',
      border: `1px solid ${colors.border.light}`
    }}>
      {/* Header */
      }
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '8px', justifyContent: 'space-between' }}>
        <Typography sx={{
          fontSize: '9px',
          color: colors.text.primary,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
          lineHeight: 1
        }}>
          {isLaneExpansion ? t('suggestedLaneExpansion') : t('suggestedReplacement')}
        </Typography>
        {isLaneExpansion && laneCount > 0 && (
          <Typography sx={{
            fontSize: '9px',
            fontWeight: 700,
            color: '#3730a3',
            backgroundColor: '#eef2ff',
            border: '1px solid #c7d2fe',
            borderRadius: '999px',
            px: '6px',
            py: '1px',
            lineHeight: 1.2,
            flexShrink: 0
          }}>
            {t('laneChip', { count: laneCount })}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '6px' }}>
        <Tooltip title={productName} arrow placement="top">
          <Typography sx={{
            fontSize: '10px',
            color: colors.text.primary,
            fontWeight: 500,
            lineHeight: 1.2,
            maxWidth: '125px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {productName}
          </Typography>
        </Tooltip>
        <Typography sx={{
          fontSize: '10px',
          color: colors.text.primary,
          fontWeight: 500,
          lineHeight: 1.2,
          flexShrink: 0
        }}>
          →
        </Typography>
        {isEditable ? (
          <ComboDropdown
            comboPrices={experiment.comboPrices || []}
            selectedCombo={selectedCombo}
            onComboSelect={onComboSelect}
            onSelectNewProduct={onSelectNewProduct}
            productKey={productKey}
            disabled={false}
            t={t}
            alternatives={isReplacement ? alternativeOptions(replacementAlternatives || {}) : undefined}
            quadrant={replacementAlternatives?.quadrant}
            onOpen={isReplacement ? () => setAlternativesOpened(true) : undefined}
          />
        ) : (
          <Tooltip title={candidateName} arrow placement="top">
            <Typography sx={{
              fontSize: '10px',
              color: colors.text.primary,
              fontWeight: 700,
              lineHeight: 1.2,
              backgroundColor: colors.background.surface,
              border: `1px solid ${colors.border.light}`,
              borderRadius: '4px',
              px: '4px',
              py: '1px',
              maxWidth: '130px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {candidateName || 'No candidate'}
            </Typography>
          </Tooltip>
        )}
        
        {/* Save button - show when candidate has been modified (same pattern as DiscountInput) */}
        {pendingSaves[productKey] && (
          <Box
            onMouseDown={(e) => {
              // Capture main scroll container position before save
              const mainScrollContainer = document.querySelector('.css-4z6nar');
              if (mainScrollContainer) {
                window.savedColumnScroll = {
                  scrollTop: mainScrollContainer.scrollTop
                };
              }
            }}
            onClick={() => onSaveChanges(productKey)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              backgroundColor: colors.action.primary,
              color: 'white',
              borderRadius: '4px',
              px: '4px',
              py: '1px',
              cursor: 'pointer',
              fontSize: '9px',
              fontWeight: 500,
              lineHeight: 1,
              ml: '2px',
              transition: 'all 0.2s ease',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              '&:hover': {
                backgroundColor: colors.action.primaryDark || '#1976d2',
                transform: 'translateY(-0.5px)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
              },
              '&:active': {
                transform: 'translateY(0px)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }
            }}
            title="Save changes"
          >
            <Save sx={{ fontSize: '8px' }} />
            <Typography sx={{ 
              fontSize: '9px', 
              fontWeight: 500,
              color: 'white',
              lineHeight: 1
            }}>
              {t('save')}
            </Typography>
          </Box>
        )}
      </Box>
      {updatedStates[productKey] && (
        <Typography sx={{
          fontSize: '9px',
          color: experimentColors.running,
          fontWeight: 600,
          mt: '2px',
          backgroundColor: `${experimentColors.running}15`,
          px: '4px',
          py: '1px',
          borderRadius: '4px'
        }}>
          ✓ Updated
        </Typography>
      )}
      
      {/* Product Selection Inline UI */}
      {showProductSelection && showProductSelection[productKey] && (
        <ProductSelectionInline
          show={showProductSelection[productKey]}
          onApply={(productId, productName, reason, price) => 
            onCustomProductSelect(productKey, productId, productName, reason, price, item, {[productKey]: {candidates: [{name: selectedCombo?.name}]}}, selectedCombo)
          }
          onCancel={() => onCancelProductSelection(productKey)}
          t={t}
          clientId={clientId}
        />
      )}
      
    </Box>
  );
};

export default ReplacementSection;
